import React, { FunctionComponent } from 'react';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { fetch_one } from '../../../../lib/v31lib';
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import HelpersLanguage from '../../../../actions/helpers/language';
import HelpersPractice from '../../../../actions/helpers/practice';

import { User } from '../../../../models/User';
import { AdhProfile } from '../../../../models/AdhProfile';

const { DateTime } = require("luxon");

type Props = {
  object: User;
}

const selectContentWithId = (field?: {[z:string]: string}, extraFallback?: string) => {
  let correct = (field || {})[HelpersLanguage.mapping_to_id(localStorage.getItem('i18nextLng') || 'nl')];
  return correct || (field || {})['1'] || (field || {})['nl'] || Object.values(field || {}).filter(fff => !!fff)[0] || extraFallback || '';
}

const fetchProfile = (login: any, i18n: any, id: number) => {
  return fetch_one<AdhProfile>(
    `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
    {
      object: 'adh_profile',
      fields: ['id', 'name', 'full_name', 'source', 'source_date', 'derived_id', 'derived'],
      sub_fields: {
        derived: ['id', 'name', 'full_name']
      },
      id: id
    },
    login
  );
}

export const CoachUserWidgetsCalculation: FunctionComponent<Props> = ({
  object
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  const is_calculated_diary = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'calculated_diary'});

  const consentAdhId = ((object?.consents || []).find(ccc => !!ccc.enabled && !!ccc.adh_profile_id) || {}).adh_profile_id;
  const activeAdhId = consentAdhId || object?.adh_profile_id || object?.calculated_adh_profile_id;
  const isOwnChoice = !consentAdhId && !!object?.adh_profile_id;

  const {
    data: profile,
    isLoading: profileLoading,
  } = useQuery({
    queryKey: ["calculation_w", "adh_profile", activeAdhId],
    queryFn: () => fetchProfile(login, i18n, activeAdhId as number),
    enabled: !!object?.id && object?.type === "User::User" && !login?.is_secretary && !!activeAdhId && !!is_calculated_diary,
  });

  if (!is_calculated_diary) return null;

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    <Button
      label={t("coach.calculation.open")}
      id="goto_calculation"
      text
      size="small"
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object?.id}/calculation`);
      }}
    />
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.calculation")}</Typography>
        <Divider />
      </Grid>

      {!activeAdhId && <Grid item xs={12}>
        <Typography variant="body2" sx={{fontStyle: 'italic'}}>{t("coach.calculation.no_profile")}</Typography>
      </Grid>}

      {!!activeAdhId && profileLoading && <Grid item xs={12}>
        <Typography variant="body2" sx={{fontStyle: 'italic'}}>{t("general.loading", "Laden...")}</Typography>
      </Grid>}

      {!!activeAdhId && !profileLoading && !profile && <Grid item xs={12}>
        <Typography variant="body2" sx={{fontStyle: 'italic'}}>{t("coach.calculation.no_profile")}</Typography>
      </Grid>}

      {!!activeAdhId && !!profile && <>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{fontWeight: 'bold'}}>{selectContentWithId(profile.full_name, profile.name)}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption">{t("coach.calculation.active_profile")}: {isOwnChoice ? t("coach.calculation.profile_choice") : t("coach.calculation.profile_calculated")}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption">{t("coach.calculation.source")}: {profile.source || '-'}{!!profile.source_date ? ` (${DateTime.fromISO(profile.source_date).toFormat("dd/LL/y")})` : ''}</Typography>
        </Grid>
        {!!profile.derived && <Grid item xs={12}>
          <Typography variant="caption">{t("coach.calculation.based_on")}: {selectContentWithId(profile.derived.full_name, profile.derived.name)}</Typography>
        </Grid>}
      </>}
    </Grid>
  </Paper>;
}
